import { md5, sha1 } from "@noble/hashes/legacy.js";
import { sha256, sha512 } from "@noble/hashes/sha2.js";
import JSZip, { type JSZipObject } from "jszip";

import { classifyEntryPath, type DetectedFileFormat } from "./file-formats";
import { ensureNotAborted, reportProgress } from "./scan-runtime";
import type { ArchiveEntry, ScanProgress } from "./types";

/**
 * Reading an archive into facts: digests, sizes, entry kinds, layout digests,
 * binary string extraction, and the small resource formats JarLab understands.
 *
 * Everything here runs before detection and produces no findings. It was
 * interleaved with the scan lifecycle inside `analyzer.ts`, which made "where
 * does an entry get its kind" and "where does the scan decide a verdict" the
 * same file and the same question.
 *
 * The block was moved verbatim. No digest algorithm, size limit,
 * classification rule, binary string threshold or property-parsing behaviour
 * changed in this split.
 */

type ZipObjectWithSizes = JSZipObject & {
  _data?: { compressedSize?: number; uncompressedSize?: number };
};

const syntheticZipSizes = new WeakMap<
  JSZipObject,
  { compressedSize: number; uncompressedSize: number }
>();
const syntheticZipKinds = new WeakMap<JSZipObject, ArchiveEntry["kind"]>();

/**
 * Register an entry JarLab built in memory. Such an entry has no JSZip
 * `_data`, so `zipSize` and `classifyZipObject` cannot read its size or kind
 * from it; these maps are where they look instead. This is archive-reading
 * state rather than scan state, and it used to sit in `analyzer.ts` beside the
 * two functions that read it.
 *
 * Exported as a registrar rather than as the maps, so there is one way in.
 */
export function registerSyntheticZipEntry(
  entry: JSZipObject,
  sizes: { compressedSize: number; uncompressedSize: number },
  kind: ArchiveEntry["kind"],
) {
  syntheticZipSizes.set(entry, sizes);
  syntheticZipKinds.set(entry, kind);
}


export function formatBytes(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

export function zipSize(entry: JSZipObject, kind: "compressed" | "uncompressed") {
  const synthetic = syntheticZipSizes.get(entry);
  if (synthetic) {
    return kind === "compressed"
      ? synthetic.compressedSize
      : synthetic.uncompressedSize;
  }
  const data = (entry as ZipObjectWithSizes)._data;
  return kind === "compressed"
    ? (data?.compressedSize ?? 0)
    : (data?.uncompressedSize ?? 0);
}

export function classifyEntry(path: string, directory: boolean): ArchiveEntry["kind"] {
  return classifyEntryPath(path, directory);
}

export function classifyZipObject(entry: JSZipObject) {
  return (
    syntheticZipKinds.get(entry) ?? classifyEntry(entry.name, entry.dir)
  );
}

export function digest(
  algorithm: "MD5" | "SHA-1" | "SHA-256" | "SHA-512",
  bytes: Uint8Array,
) {
  const result =
    algorithm === "SHA-512"
      ? sha512(bytes)
      : algorithm === "SHA-256"
      ? sha256(bytes)
      : algorithm === "SHA-1"
        ? sha1(bytes)
        : md5(bytes);
  return Array.from(result)
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
}

export interface AnalyzeOptions {
  officialLookup?: boolean;
}

export function archiveLayoutDigest(entries: ArchiveEntry[]) {
  const inventory = entries
    .filter((entry) => !entry.directory)
    .map(
      (entry) =>
        `${entry.path.replaceAll("\\", "/")}\0${entry.size}\0${entry.compressedSize}\0${entry.kind}`,
    )
    .sort()
    .join("\n");
  return digest("SHA-256", new TextEncoder().encode(inventory));
}

export function extractBinaryStrings(bytes: Uint8Array, limit = 2_000) {
  const strings: string[] = [];
  let ascii = "";
  const pushAscii = () => {
    if (ascii.length >= 6 && strings.length < limit) strings.push(ascii);
    ascii = "";
  };

  for (const byte of bytes) {
    if (byte >= 32 && byte <= 126) ascii += String.fromCharCode(byte);
    else pushAscii();
    if (strings.length >= limit) break;
  }
  pushAscii();

  let utf16 = "";
  for (let index = 0; index + 1 < bytes.length && strings.length < limit; ) {
    if (bytes[index] >= 32 && bytes[index] <= 126 && bytes[index + 1] === 0) {
      utf16 += String.fromCharCode(bytes[index]);
      index += 2;
    } else {
      if (utf16.length >= 6) strings.push(utf16);
      utf16 = "";
      index += 1;
    }
  }
  if (utf16.length >= 6 && strings.length < limit) strings.push(utf16);
  return Array.from(new Set(strings));
}

export function parseManifest(raw: string) {
  const unfolded = raw.replace(/\r?\n /g, "");
  const output: Record<string, string> = {};
  for (const line of unfolded.split(/\r?\n/)) {
    const separator = line.indexOf(":");
    if (separator <= 0) continue;
    output[line.slice(0, separator).trim()] = line.slice(separator + 1).trim();
  }
  return output;
}

export function parseProperties(raw: string) {
  const output: Record<string, string> = {};
  for (const line of raw.split(/\r?\n/)) {
    if (!line || /^\s*[#!]/.test(line)) continue;
    const separator = line.search(/[=:]/);
    if (separator < 0) continue;
    output[line.slice(0, separator).trim()] = line
      .slice(separator + 1)
      .trim();
  }
  return output;
}

// Container limits, here rather than in the phase that enforces them: both the
// step that opens the container and the walk that opens nested archives inside
// it have to agree on them, and this is the one module both already import.
export interface ContainerInput {
  /** The bytes as handed to the scanner, before any unwrapping. */
  uploadedBytes: Uint8Array;
  fileName: string;
  lowerName: string;
  /** `detectFileFormat(uploadedBytes, fileName)` — the caller identifies, we open. */
  format: DetectedFileFormat;
  onProgress?: (progress: ScanProgress) => void;
  signal?: AbortSignal;
}

/** What the rest of the scan learns from the container itself. */
export interface OpenedContainer {
  archive: JSZip;
  zipObjects: JSZipObject[];
  /** The bytes the scan analyses: the inner JAR when one was unwrapped. */
  analyzedBytes: Uint8Array;
  analyzedFileName: string;
  innerArchive: string | undefined;
  containerType: string;
  containerReadWarning: string;
  unsafePaths: string[];
  totalUncompressed: number;
}

/**
 * Open the container: read the zip, unwrap a jar out of a zip-like wrapper,
 * and enforce the two limits that are about the container rather than its
 * contents.
 *
 * This is phase 1b of the scan. It lived inside `analyzeJar` while
 * `archive-ingest.ts` exported only the primitives it calls, so "what is in
 * this container" had an owner in name and a different one in practice. It is
 * here now, and `analyzeJar` holds nothing but the call.
 *
 * `rawWrapped` deliberately does not survive: nothing after this point reads
 * it, so it stays local rather than becoming a field callers must ignore.
 */
export async function openContainer(
  input: ContainerInput,
): Promise<OpenedContainer> {
  const { uploadedBytes, fileName, lowerName, format, onProgress, signal } =
    input;
  // A .mcpack is a zip holding a jar, and a single-class upload is not an
  // archive at all. Both answers change which bytes the rest of the scan sees,
  // so this happens before anything else is derived from them.
  let analyzedBytes = uploadedBytes;
  let analyzedFileName = fileName;
  let innerArchive: string | undefined;
  let containerType = format.label;
  let containerReadWarning = "";
  let rawWrapped = false;

  reportProgress(
    onProgress,
    "Unpacking container",
    10,
    format.container
      ? `Validating ${format.label}`
      : `Indexing standalone ${format.label}`,
  );
  let archive: JSZip;
  const isPkZip =
    uploadedBytes.length >= 4 &&
    uploadedBytes[0] === 0x50 &&
    uploadedBytes[1] === 0x4b &&
    ((uploadedBytes[2] === 0x03 && uploadedBytes[3] === 0x04) ||
      (uploadedBytes[2] === 0x05 && uploadedBytes[3] === 0x06) ||
      (uploadedBytes[2] === 0x07 && uploadedBytes[3] === 0x08));
  if (isPkZip) {
    try {
      archive = await JSZip.loadAsync(uploadedBytes, {
        checkCRC32: false,
        createFolders: true,
      });
    } catch {
      archive = new JSZip();
      archive.file(fileName, uploadedBytes);
      const entry = archive.file(fileName);
      if (entry) {
        registerSyntheticZipEntry(
          entry,
          {
            compressedSize: uploadedBytes.length,
            uncompressedSize: uploadedBytes.length,
          },
          format.category,
        );
      }
      rawWrapped = true;
      containerReadWarning =
        "The outer container could not be indexed by the primary ZIP parser. It remains available as a standalone artifact for password recovery and format-aware unpacking.";
    }
  } else {
    archive = new JSZip();
    archive.file(fileName, uploadedBytes);
    const entry = archive.file(fileName);
    if (entry) {
      registerSyntheticZipEntry(
        entry,
        {
          compressedSize: uploadedBytes.length,
          uncompressedSize: uploadedBytes.length,
        },
        format.category,
      );
    }
    rawWrapped = true;
  }
  ensureNotAborted(signal);

  if (!rawWrapped && /\.(?:zip|mcpack|mrpack)$/i.test(lowerName)) {
    const innerJars = Object.values(archive.files)
      .filter((entry) => !entry.dir && entry.name.toLowerCase().endsWith(".jar"))
      .sort(
        (left, right) =>
          zipSize(right, "uncompressed") - zipSize(left, "uncompressed"),
      );
    if (innerJars.length) {
      reportProgress(
        onProgress,
        "Unpacking container",
        16,
        `Extracting ${innerJars[0].name}`,
      );
      analyzedBytes = new Uint8Array(await innerJars[0].async("uint8array"));
      if (analyzedBytes.byteLength > MAX_UNCOMPRESSED_BYTES) {
        throw new Error("The inner JAR exceeds the safe extraction limit.");
      }
      innerArchive = innerJars[0].name;
      analyzedFileName = innerJars[0].name.split("/").pop() ?? innerJars[0].name;
      archive = await JSZip.loadAsync(analyzedBytes, {
        checkCRC32: false,
        createFolders: true,
      });
      containerType = `${format.label} container`;
    }
  }

  const zipObjects = Object.values(archive.files);
  if (zipObjects.length > MAX_ENTRIES) {
    throw new Error(
      `Archive contains ${zipObjects.length.toLocaleString()} entries; the safe limit is ${MAX_ENTRIES.toLocaleString()}.`,
    );
  }

  const unsafePaths = zipObjects
    .map((entry) => {
      const originalName = (
        entry as JSZipObject & { unsafeOriginalName?: string }
      ).unsafeOriginalName;
      return originalName ?? entry.name;
    })
    .filter((name) => name.split(/[\\/]/).some((segment) => segment === ".."));
  const totalUncompressed = zipObjects.reduce(
    (sum, entry) => sum + zipSize(entry, "uncompressed"),
    0,
  );
  if (totalUncompressed > MAX_UNCOMPRESSED_BYTES) {
    throw new Error(
      `Archive expands to ${formatBytes(totalUncompressed)}; the safe limit is 300 MB.`,
    );
  }

  return {
    archive,
    zipObjects,
    analyzedBytes,
    analyzedFileName,
    innerArchive,
    containerType,
    containerReadWarning,
    unsafePaths,
    totalUncompressed,
  };
}

export const MAX_ENTRIES = 16_000;
export const MAX_UNCOMPRESSED_BYTES = 300 * 1024 * 1024;
